import React from 'react';

/**
 * Custom React hook to attach a non-passive wheel event listener
 * to an HTMLInputElement via a callback ref.
 *
 * This hook ensures that the provided `callback` is invoked whenever
 * the user scrolls with the mouse wheel over the input element.
 *
 * The event listener is registered with `{ passive: false }` to allow
 * calling `event.preventDefault()` inside the handler.
 *
 * Why `useCallback` instead of `useRef`:
 * - `useCallback` returns a **ref callback** (a function), which React automatically
 *   calls when the DOM node mounts or changes.
 * - This makes it ideal for attaching event listeners immediately upon node assignment,
 *   without needing a `useEffect` that depends on a `ref.current`.
 * - `useRef` simply holds a reference to a DOM node, but does not notify you when
 *   that node becomes available — so you'd need to manually manage effects to attach listeners.
 *
 * See this blog posts:
 * - https://www.robinwieruch.de/react-ref/ (scroll to React Callback Ref section)
 *
 * @param {function} callback - A function that will be called on 'wheel' events.
 *                              Receives the native `WheelEvent` as its argument.
 *
 * @returns {function} - A ref callback to be passed to an input element.
 *                       Internally handles attaching the wheel event.
 *
 * @example
 * const wheelRef = useMouseWheel((e) => {
 *   e.preventDefault();
 *   console.log('Wheel scrolled!');
 * });
 *
 * return <input ref={wheelRef} />
 */
export const useMouseWheel = (
  callback: (event: WheelEvent) => void
): React.RefCallback<HTMLInputElement> => {
  const prevNodeRef = React.useRef<HTMLInputElement | null>(null);

  const wheelRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      if (prevNodeRef.current) {
        prevNodeRef.current.removeEventListener('wheel', callback, {
          capture: false,
        });
      }

      if (node) {
        const handleWheel = (event: WheelEvent) => {
          event.stopPropagation();
          callback(event);
        };

        node.addEventListener('wheel', handleWheel, {
          passive: false,
          capture: true,
        });
      }

      prevNodeRef.current = node;
    },
    [callback]
  );

  return wheelRef;
};
